import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import type {
  PermissionCatalogEntry,
  RoleWithPermissions,
  RolesResponse,
} from "@forgepoint/shared";

/** Per-role loading/error flags, keyed by role id in the store. */
export interface RoleLoadingState {
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  saveError: string | null;
}

interface RolesStore {
  /** Map of roleId -> role, kept current across fetches and mutations. */
  roleMap: Map<string, RoleWithPermissions>;
  /** The permission primitives available in the role editor, grouped by area. */
  permissionCatalog: PermissionCatalogEntry[];
  /** Per-role save/delete loading states, keyed by role id. */
  loadingById: Record<string, RoleLoadingState>;
  isLoadingList: boolean;
  isLoadingCreate: boolean;
  error: string | null;

  fetchRoles: () => Promise<void>;
  fetchCatalog: () => Promise<void>;
  createRole: (
    name: string,
    permissions: string[],
  ) => Promise<RoleWithPermissions | null>;
  updateRole: (
    roleId: string,
    updates: { name?: string; permissions?: string[] },
  ) => Promise<RoleWithPermissions | null>;
  deleteRole: (roleId: string) => Promise<boolean>;
}

const IDLE_LOADING_STATE: RoleLoadingState = {
  isLoadingSave: false,
  isLoadingDelete: false,
  saveError: null,
};

/**
 * Store of the organization's roles (id-keyed map) and the permission catalog,
 * backed by the /roles and /permissions REST endpoints. Fetching the list
 * stops after a failure so a broken endpoint is not retried on every render.
 */
export const useRolesStore = create<RolesStore>((set) => ({
  roleMap: new Map(),
  permissionCatalog: [],
  loadingById: {},
  isLoadingList: false,
  isLoadingCreate: false,
  error: null,

  fetchRoles: async () => {
    set({ isLoadingList: true, error: null });
    try {
      const response = await fetchApi("roles", "GET");
      if (response.ok && response.data) {
        const data = response.data as RolesResponse;
        const roleMap = new Map(data.roles.map((role) => [role.id, role]));
        set({ roleMap, isLoadingList: false });
        return;
      }
      set({ error: "Failed to fetch roles", isLoadingList: false });
    } catch (error) {
      captureException(error as Error);
      set({ error: "Failed to fetch roles", isLoadingList: false });
    }
  },

  fetchCatalog: async () => {
    try {
      const response = await fetchApi("permissions", "GET");
      if (response.ok && response.data) {
        const data = response.data as {
          permissions: PermissionCatalogEntry[];
        };
        set({ permissionCatalog: data.permissions });
        return;
      }
      set({ error: "Failed to fetch permissions" });
    } catch (error) {
      captureException(error as Error);
      set({ error: "Failed to fetch permissions" });
    }
  },

  createRole: async (name, permissions) => {
    set({ isLoadingCreate: true, error: null });
    try {
      const response = await fetchApi("roles", "POST", {
        body: { name, permissions },
      });
      if (response.ok && response.data) {
        const data = response.data as { role: RoleWithPermissions };
        set((state) => ({
          roleMap: new Map(state.roleMap).set(data.role.id, data.role),
          isLoadingCreate: false,
        }));
        return data.role;
      }
      set({
        error: response.error ?? "Failed to create role",
        isLoadingCreate: false,
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set({ error: "Failed to create role", isLoadingCreate: false });
      return null;
    }
  },

  updateRole: async (roleId, updates) => {
    setRoleLoading(set, roleId, { isLoadingSave: true, saveError: null });
    try {
      const response = await fetchApi(`roles/${roleId}`, "PUT", {
        body: updates,
      });
      if (response.ok && response.data) {
        const data = response.data as { role: RoleWithPermissions };
        set((state) => ({
          roleMap: new Map(state.roleMap).set(data.role.id, data.role),
        }));
        setRoleLoading(set, roleId, { isLoadingSave: false });
        return data.role;
      }
      setRoleLoading(set, roleId, {
        isLoadingSave: false,
        saveError: response.error ?? "Failed to update role",
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      setRoleLoading(set, roleId, {
        isLoadingSave: false,
        saveError: "Failed to update role",
      });
      return null;
    }
  },

  deleteRole: async (roleId) => {
    setRoleLoading(set, roleId, { isLoadingDelete: true, saveError: null });
    try {
      const response = await fetchApi(`roles/${roleId}`, "DELETE");
      if (response.ok) {
        set((state) => {
          const roleMap = new Map(state.roleMap);
          roleMap.delete(roleId);
          const loadingById = Object.fromEntries(
            Object.entries(state.loadingById).filter(([id]) => id !== roleId),
          );
          return { roleMap, loadingById };
        });
        return true;
      }
      setRoleLoading(set, roleId, {
        isLoadingDelete: false,
        saveError: response.error ?? "Failed to delete role",
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      setRoleLoading(set, roleId, {
        isLoadingDelete: false,
        saveError: "Failed to delete role",
      });
      return false;
    }
  },
}));

/**
 * Merges partial loading flags into a role's per-id loading state.
 * @param set - The store's set function
 * @param roleId - The role whose loading state to update
 * @param partial - The flags to merge over the current state
 */
function setRoleLoading(
  set: (fn: (state: RolesStore) => Partial<RolesStore>) => void,
  roleId: string,
  partial: Partial<RoleLoadingState>,
): void {
  set((state) => ({
    loadingById: {
      ...state.loadingById,
      [roleId]: {
        ...(state.loadingById[roleId] ?? IDLE_LOADING_STATE),
        ...partial,
      },
    },
  }));
}

/**
 * Hook returning the organization's roles sorted with defaults first, fetching
 * them once on first use. Fetching stops after a failure so a broken endpoint
 * is not retried on every render.
 * @returns The roles list (empty until loaded)
 */
export function useRoles(): RoleWithPermissions[] {
  const roleMap = useRolesStore((state) => state.roleMap);
  const isLoadingList = useRolesStore((state) => state.isLoadingList);
  const error = useRolesStore((state) => state.error);
  const fetchRoles = useRolesStore((state) => state.fetchRoles);

  const roles = useMemo(() => sortRoles(roleMap), [roleMap]);

  useEffect(() => {
    if (roleMap.size === 0 && !isLoadingList && !error) {
      fetchRoles();
    }
  }, [roleMap.size, isLoadingList, error, fetchRoles]);

  return roles;
}

/**
 * Sorts roles for display: default roles first, then custom roles by name.
 * @param roleMap - The id-keyed role map
 * @returns The sorted roles list
 */
export function sortRoles(
  roleMap: Map<string, RoleWithPermissions>,
): RoleWithPermissions[] {
  return Array.from(roleMap.values()).sort((a, b) => {
    if (a.isDefault !== b.isDefault) {
      return a.isDefault ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });
}
